import { Component } from "react";
import { AlertTriangle, RotateCw } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Wraps the Flow Builder UI so a runtime error inside FlowCanvas / NewFlowWizard
 * doesn't blow up the entire app — instead the user sees a recoverable panel
 * with a Reload button.
 */
export class FlowErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("FlowBuilder crashed:", error, info);
  }
  reset = () => this.setState({ error: null });
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="p-8 max-w-xl mx-auto mt-16" data-testid="flow-error-boundary">
        <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50/60 dark:bg-red-950/30 p-6">
          <div className="flex items-center gap-2 text-red-700 dark:text-red-300 font-semibold">
            <AlertTriangle size={18} /> Flow Builder ran into a problem
          </div>
          <p className="text-sm text-slate-700 dark:text-slate-300 mt-2">
            Something unexpected happened. Your latest unsaved changes may be lost. The error has been logged.
          </p>
          <pre className="mt-3 text-[11px] font-mono text-slate-600 dark:text-slate-400 bg-white/60 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 rounded p-2 overflow-x-auto">
            {(this.state.error?.message || String(this.state.error)).slice(0, 400)}
          </pre>
          <div className="mt-4 flex gap-2">
            <Button onClick={this.reset} className="bg-[#8B1A1A] hover:bg-[#6f1414] text-white" data-testid="flow-error-reload-btn">
              <RotateCw size={14} className="mr-1.5" /> Try again
            </Button>
            <Button variant="outline" onClick={() => window.location.reload()} data-testid="flow-error-full-reload-btn">
              Full reload
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

export default FlowErrorBoundary;
